import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Tela1 from './pages/Tela1';
import Tela1One from './pages/Tela1-1';
import PredictivePreloader from './components/PredictivePreloader';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineAwareWrapper from './components/OfflineAwareWrapper';
import AppInitializer from './components/AppInitializer';

// Lazy load all screens except the entry point (Tela1)
const Tela2 = lazy(() => import('./pages/Tela2'));
const Tela3 = lazy(() => import('./pages/Tela3'));
const Tela4 = lazy(() => import('./pages/Tela4'));
const Tela5 = lazy(() => import('./pages/Tela5-0-categorias'));
const Tela6 = lazy(() => import('./pages/Tela6'));
const TelaPlayTemas = lazy(() => import('./pages/Tela7-0-play'));
const DiagnosticsPage = lazy(() => import('./pages/Diagnostics'));

// Lazy load all theme screens
const TemaComidas = lazy(() => import('./pages/Tela5-1-temas/Comidas'));
const TemaCores = lazy(() => import('./pages/Tela5-1-temas/Cores'));
const TemaLetras = lazy(() => import('./pages/Tela5-1-temas/OLD_Letras'));
const TemaNumeros = lazy(() => import('./pages/Tela5-1-temas/Numeros'));
const TemaAnimais = lazy(() => import('./pages/Tela5-1-temas/OLD_Animais'));
const TemaRoupas = lazy(() => import('./pages/Tela5-1-temas/Roupas'));
const TemaWords = lazy(() => import('./pages/Tela5-1-temas/Letras'));
const TemaAnimals = lazy(() => import('./pages/Tela5-1-temas/Animais'));

// Definição de rotas com metadados para uso offline
const appRoutes = [
  { path: '/', name: 'Início', priority: 'high' },
  { path: '/tela1-1', name: 'Introdução', priority: 'high' },
  { path: '/tela2', name: 'Menu Principal', priority: 'high' },
  { path: '/tela3', name: 'Tela 3', priority: 'medium' },
  { path: '/tela4', name: 'Tela 4', priority: 'medium' },
  { path: '/tela5', name: 'Categorias', priority: 'high' },
  { path: '/tela6', name: 'Tela 6', priority: 'medium' },
  { path: '/tela7', name: 'Jogar', priority: 'high' },
  { path: '/tema-letras', name: 'Letras', priority: 'medium' },
  { path: '/tema-animais', name: 'Animais', priority: 'medium' },
  { path: '/tema-comidas', name: 'Comidas', priority: 'medium' },
  { path: '/tema-cores', name: 'Cores', priority: 'medium' },
  { path: '/tema-numeros', name: 'Números', priority: 'medium' },
  { path: '/tema-roupas', name: 'Roupas', priority: 'medium' },
  { path: '/tema-words', name: 'Palavras', priority: 'medium' },
  { path: '/tema-animais', name: 'Animals', priority: 'medium' },
  { path: '/tela-play-temas', name: 'Jogar Temas', priority: 'high' },
  { path: '/diagnostics', name: 'Diagnósticos', priority: 'low' }
];

// Loading fallback component
const LoadingFallback = () => (
  <div className="loading-fallback"></div>
);

function App() {
  // Handler para quando a inicialização for concluída
  const handleAppInitialized = () => {
    // Inicialização concluída
    console.log('Aplicação inicializada com sucesso');
  };
  
  // Configurar manipuladores globais de erro
  useEffect(() => {
    // Capturar erros não tratados em Promises
    const handleUnhandledRejection = (event) => {
      console.error('Erro de Promise não tratado:', event.reason);
      // Evita que o erro propague e cause crash na aplicação
      event.preventDefault();
    };

    // Capturar erros globais de JavaScript
    const handleGlobalError = (event) => {
      console.error('Erro global capturado:', event.error || event.message);
      // Evita que o erro padrão seja exibido
      event.preventDefault();
    };

    // Adicionar listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    // Cleanup dos listeners quando o componente for desmontado
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AppInitializer onInitialized={handleAppInitialized}>
        <Router>
          {/* Componente de precarregamento preditivo */}
          <PredictivePreloader />
          
          {/* Wrapper para tratamento de estado offline/online */}
          <OfflineAwareWrapper routes={appRoutes}>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Tela1 />} />
                <Route path="/tela1-1" element={<Tela1One />} />
                <Route path="/tela2" element={
                  <ErrorBoundary>
                    <Tela2 />
                  </ErrorBoundary>
                } />
                <Route path="/tela3" element={
                  <ErrorBoundary>
                    <Tela3 />
                  </ErrorBoundary>
                } />
                <Route path="/tela4" element={
                  <ErrorBoundary>
                    <Tela4 />
                  </ErrorBoundary>
                } />
                <Route path="/tela5" element={
                  <ErrorBoundary>
                    <Tela5 />
                  </ErrorBoundary>
                } />
                <Route path="/tela6" element={
                  <ErrorBoundary>
                    <Tela6 />
                  </ErrorBoundary>
                } />
                <Route path="/tela7" element={
                  <ErrorBoundary>
                    <TelaPlayTemas />
                  </ErrorBoundary>
                } />
                <Route path="/tema-letras" element={
                  <ErrorBoundary>
                    <TemaLetras />
                  </ErrorBoundary>
                } />
                <Route path="/tema-animais" element={
                  <ErrorBoundary>
                    <TemaAnimals />
                  </ErrorBoundary>
                } />
                <Route path="/tema-comidas" element={
                  <ErrorBoundary>
                    <TemaComidas />
                  </ErrorBoundary>
                } />
                <Route path="/tema-cores" element={
                  <ErrorBoundary>
                    <TemaCores />
                  </ErrorBoundary>
                } />
                <Route path="/tema-numeros" element={
                  <ErrorBoundary>
                    <TemaNumeros />
                  </ErrorBoundary>
                } />
                <Route path="/tema-roupas" element={
                  <ErrorBoundary>
                    <TemaRoupas />
                  </ErrorBoundary>
                } />
                <Route path="/tema-words" element={
                  <ErrorBoundary>
                    <TemaWords />
                  </ErrorBoundary>
                } />
                <Route path="/tela-play-temas" element={
                  <ErrorBoundary>
                    <TelaPlayTemas />
                  </ErrorBoundary>
                } />
                <Route path="/diagnostics" element={
                  <ErrorBoundary>
                    <DiagnosticsPage />
                  </ErrorBoundary>
                } />
              </Routes>
            </Suspense>
          </OfflineAwareWrapper>
        </Router>
      </AppInitializer>
    </ErrorBoundary>
  );
}

export default App;
